import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DEFAULT_OPTIONS,
  FACETS,
  generateDossier
} from "../generator";
import { validateDossier } from "../generator/validate";
import { dossierIdFromLocation, fetchDossier } from "../lib/api";
import { loadLocalState, saveLocalState } from "../lib/localDb";
import type {
  DragonDossier,
  Facet,
  GeneratorOptions,
  ValidationIssue
} from "../types";

interface HistoryState {
  past: DragonDossier[];
  present: DragonDossier;
  future: DragonDossier[];
}

const CASCADE_FACETS: Partial<Record<Facet, Facet[]>> = {
  lair: ["history", "encounter", "hooks"],
  hoard: ["history", "encounter", "hooks"],
  factions: ["hoard", "history", "encounter", "hooks"]
};

function preserveFacet(target: DragonDossier, source: DragonDossier, facet: Facet): void {
  switch (facet) {
    case "identity":
      target.dragon = structuredClone(source.dragon);
      target.appearance = structuredClone(source.appearance);
      target.assets = structuredClone(source.assets);
      break;
    case "rules":
      target.rules = structuredClone(source.rules);
      break;
    case "lair":
      target.lair = structuredClone(source.lair);
      target.regionalEffects = structuredClone(source.regionalEffects);
      break;
    case "hoard":
      target.hoard = structuredClone(source.hoard);
      break;
    case "factions":
      target.factions = structuredClone(source.factions);
      target.relationships = structuredClone(source.relationships);
      break;
    case "history":
      target.history = structuredClone(source.history);
      break;
    case "encounter":
      target.encounterScript = structuredClone(source.encounterScript);
      break;
    case "hooks":
      target.hooks = structuredClone(source.hooks);
      break;
  }
}

export function useWorkbench() {
  const [options, setOptions] = useState<GeneratorOptions>(DEFAULT_OPTIONS);
  const [history, setHistory] = useState<HistoryState>(() => ({
    past: [],
    present: generateDossier(DEFAULT_OPTIONS),
    future: []
  }));
  const [selectedRoomId, setSelectedRoomId] = useState<string>(
    history.present.lair.rooms.find((room) => room.role === "hoard")?.id ?? history.present.lair.rooms[0].id
  );
  const [selectedFactionId, setSelectedFactionId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [ready, setReady] = useState(false);
  const [status, setStatus] = useState("Ready");
  const saveTimer = useRef<number | null>(null);

  const dossier = history.present;
  const issues = useMemo<ValidationIssue[]>(() => validateDossier(dossier), [dossier]);
  const selectedRoom =
    dossier.lair.rooms.find((room) => room.id === selectedRoomId) ?? dossier.lair.rooms[0];
  const selectedFaction =
    dossier.factions.find((faction) => faction.id === selectedFactionId) ?? null;

  const commit = useCallback((mutator: (draft: DragonDossier) => void, message = "Edited") => {
    setHistory((current) => {
      const next = structuredClone(current.present);
      mutator(next);
      next.revision = current.present.revision;
      return {
        past: [...current.past.slice(-49), current.present],
        present: next,
        future: []
      };
    });
    setStatus(message);
  }, []);

  const undo = useCallback(() => {
    setHistory((current) => {
      const previous = current.past.at(-1);
      if (!previous) return current;
      return {
        past: current.past.slice(0, -1),
        present: previous,
        future: [current.present, ...current.future]
      };
    });
    setStatus("Undo");
  }, []);

  const redo = useCallback(() => {
    setHistory((current) => {
      const next = current.future[0];
      if (!next) return current;
      return {
        past: [...current.past, current.present],
        present: next,
        future: current.future.slice(1)
      };
    });
    setStatus("Redo");
  }, []);

  const generateAll = useCallback(
    (override?: Partial<GeneratorOptions>) => {
      const nextOptions = { ...options, ...override };
      const generated = generateDossier(nextOptions);
      FACETS.forEach((facet) => {
        if (nextOptions.locks[facet]) preserveFacet(generated, dossier, facet);
      });
      generated.manifest.locks = { ...nextOptions.locks };
      setOptions(nextOptions);
      setHistory((current) => ({
        past: [...current.past.slice(-49), current.present],
        present: generated,
        future: []
      }));
      setSelectedRoomId(generated.lair.rooms.find((room) => room.role === "hoard")?.id ?? generated.lair.rooms[0].id);
      setSelectedFactionId(null);
      setStatus("Dragon generated");
    },
    [dossier, options]
  );

  const switchLineage = useCallback(
    (lineage: string) => {
      const tag = Math.random().toString(36).slice(2, 6);
      const nextOptions = { ...options, lineage, seed: `${lineage}-${tag}` };
      const generated = generateDossier(nextOptions);
      // Explicit dragon switch: always reveal the new lineage (ignore identity lock),
      // but honour any other facets the user pinned.
      FACETS.forEach((facet) => {
        if (facet !== "identity" && nextOptions.locks[facet]) {
          preserveFacet(generated, dossier, facet);
        }
      });
      generated.manifest.locks = { ...nextOptions.locks };
      setOptions(nextOptions);
      setHistory((current) => ({
        past: [...current.past.slice(-49), current.present],
        present: generated,
        future: []
      }));
      setSelectedRoomId(
        generated.lair.rooms.find((room) => room.role === "hoard")?.id ?? generated.lair.rooms[0].id
      );
      setSelectedFactionId(null);
      setStatus(`Conjured ${lineage} dragon`);
    },
    [dossier, options]
  );

  const rerollFacet = useCallback(
    (facet: Facet) => {
      if (options.locks[facet]) {
        setStatus(`${facet} is locked`);
        return;
      }
      const nextOptions: GeneratorOptions = {
        ...options,
        rerolls: {
          ...options.rerolls,
          [facet]: options.rerolls[facet] + 1
        }
      };
      const generated = generateDossier(nextOptions);
      const refreshed = new Set<Facet>([facet, ...(CASCADE_FACETS[facet] ?? [])]);
      FACETS.filter((candidate) => !refreshed.has(candidate) || options.locks[candidate]).forEach(
        (candidate) => preserveFacet(generated, dossier, candidate)
      );
      generated.manifest.rerolls = { ...nextOptions.rerolls };
      generated.manifest.locks = { ...nextOptions.locks };
      setOptions(nextOptions);
      setHistory((current) => ({
        past: [...current.past.slice(-49), current.present],
        present: generated,
        future: []
      }));
      setStatus(`${facet} rerolled`);
    },
    [dossier, options]
  );

  const toggleLock = useCallback(
    (facet: Facet) => {
      const nextLocks = { ...options.locks, [facet]: !options.locks[facet] };
      setOptions((current) => ({ ...current, locks: nextLocks }));
      commit((draft) => {
        draft.manifest.locks = nextLocks;
      }, `${facet} ${nextLocks[facet] ? "locked" : "unlocked"}`);
    },
    [commit, options.locks]
  );

  const replaceDossier = useCallback((next: DragonDossier, message = "Dossier loaded") => {
    setHistory({ past: [], present: next, future: [] });
    setOptions((current) => ({
      ...current,
      seed: next.manifest.seed,
      lineage: next.dragon.lineage,
      ageClass: next.dragon.ageClass,
      terrain: next.lair.terrain,
      adapter: next.manifest.rulesAdapter,
      locale: next.manifest.locale,
      locks: next.manifest.locks,
      rerolls: next.manifest.rerolls
    }));
    setSelectedRoomId(next.lair.rooms.find((room) => room.role === "hoard")?.id ?? next.lair.rooms[0]?.id ?? "");
    setStatus(message);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const remoteId = dossierIdFromLocation();
        if (remoteId) {
          const remote = await fetchDossier(remoteId);
          if (!cancelled) replaceDossier(remote, "Shared dossier loaded");
        } else {
          const query = new URLSearchParams(window.location.search);
          const sharedSeed = query.get("seed");
          if (sharedSeed) {
            const age = query.get("age");
            const adapter = query.get("adapter");
            const sharedOptions: GeneratorOptions = {
              ...DEFAULT_OPTIONS,
              seed: sharedSeed,
              lineage: query.get("lineage") ?? DEFAULT_OPTIONS.lineage,
              ageClass:
                age === "wyrmling" || age === "young" || age === "adult" || age === "ancient"
                  ? age
                  : DEFAULT_OPTIONS.ageClass,
              adapter:
                adapter === "neutral" || adapter === "srd51" || adapter === "srd52"
                  ? adapter
                  : DEFAULT_OPTIONS.adapter
            };
            if (!cancelled) {
              setOptions(sharedOptions);
              replaceDossier(generateDossier(sharedOptions), "Reproducible seed loaded");
            }
            return;
          }
          const local = await loadLocalState();
          if (!cancelled && local) {
            setOptions(local.options);
            replaceDossier(local.dossier, "Local draft restored");
          }
        }
      } catch (error) {
        if (!cancelled) setStatus(error instanceof Error ? error.message : "Could not restore dossier");
      } finally {
        if (!cancelled) setReady(true);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [replaceDossier]);

  useEffect(() => {
    if (!ready) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      void saveLocalState(dossier, options)
        .then(() => setStatus((current) => (current === "Edited" ? "Autosaved locally" : current)))
        .catch(() => setStatus("Local autosave unavailable"));
    }, 650);
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [dossier, options, ready]);

  return {
    options,
    setOptions,
    dossier,
    replaceDossier,
    commit,
    undo,
    redo,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
    generateAll,
    switchLineage,
    rerollFacet,
    toggleLock,
    issues,
    selectedRoom,
    selectedRoomId,
    setSelectedRoomId,
    selectedFaction,
    selectedFactionId,
    setSelectedFactionId,
    activeTab,
    setActiveTab,
    status,
    setStatus,
    ready
  };
}
